import React, { useEffect, useState, ChangeEvent } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { IoMdClose } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import { IFilters } from '../../../../../model/book/Invites';
import { IParams } from '../../../../../model/shared/IParams';
import { User } from '../../../../../model/user/User';
import { Pagination } from '../../../../../shared/components/Pagination';
import { scrollToTop } from '../../../../../utils/helpers/ScrollToTop';
import { ErrorToast } from '../../../../../utils/toasters';
import { ContractService } from '../service';
import { InviteContractCardItem } from './InviteContractCardItem';
import { InviteContractFilter } from './InviteContractFilter';
import { BackButton } from '../../../../../shared/components/button/BackButton';
import { BsSliders } from 'react-icons/bs';
import _debounce from 'lodash/debounce';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { gtmService } from '../../../../../utils/gtm.service';
import { IInviteContractFilter } from './dto/IInviteContractsFilter';
import { BookingContractModal } from '../contract/modal/BookingContractModal';
import { setInitialState } from '../../../../../store/brand/offerBrandSlice';
import { useDispatch } from 'react-redux';

export function InviteContracts() {
    const { t } = useTranslation();
    const [filterParams, setFilterParams] = useState<IInviteContractFilter>({
        page: 1,
        perPage: 10,
        sortCriteria: 'mostActive',
        sortType: 'DESC',
        platformId: [],
        reachFrom: '',
        reachTo: '',
        influencerLocation: undefined,
        audienceLocation: undefined,
        gender: '',
        influencerName: '',
        pastCampaignId: undefined,
        categoryId: undefined,
    });

    const [users, setUsers] = useState<User[]>();
    const [filterInfo, setFilterInfo] = useState<IFilters>();
    const [userCount, setUserCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const { id } = useParams<keyof IParams>();
    const [updateInvite, setUpdateInvite] = useState(false);
    const [isFilterShown, setIsFilterShown] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(0);
    const dispatch = useDispatch();

    useEffect(() => {
        if (id) {
            ContractService.getInvites(id, filterParams)
                .then(response => {
                    setFilterInfo(response.data.filters);
                    setUsers(response.data.users);
                    setTotalPages(Math.ceil(response.count / filterParams.perPage));
                    setUserCount(response.count);
                })
                .catch(error => ErrorToast(error));
        }
    }, [filterParams, updateInvite]);

    const handlePages = (updatePage: number) => {
        setFilterParams({ ...filterParams, page: updatePage });
        scrollToTop();
    };

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        setFilterParams({
            ...filterParams,
            [event.target.name]: value,
            page: 1,
        });
        gtmService('search');
    }, 300);

    // const openExternalSearchPage = () => {
    //     navigate(`${InternalRoutes.BRAND_CAMPAIGN_BOOK}/${id}/external-search`);
    // };

    const handleClose = () => {
        setUpdateInvite(!updateInvite);
        setShowContractModal(false);
        dispatch(setInitialState());
    };

    const handleShow = (userId: number) => {
        gtmService('select_content');
        setSelectedUserId(userId);
        setShowContractModal(true);
    };

    return (
        <Row>
            <BookingContractModal
                show={showContractModal}
                closeModal={handleClose}
                modalStatus={'invite'}
                userId={selectedUserId}
            />
            <Col sm={12} className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                    <Col className="d-flex pl-0">
                        <BackButton />
                        <Button variant="outline-secondary" onClick={() => setIsFilterShown(prev => !prev)}>
                            {!isFilterShown ? (
                                <BsSliders size={15} className="icon-dark" />
                            ) : (
                                <IoMdClose className="icon-dark" size={15} />
                            )}
                        </Button>
                        <Col sm={6} lg={6} xl={4} className="d-none d-sm-flex ms-3">
                            <TextField
                                label={`${t('brand.campaign.book.invite.filters.influencer_name')}`}
                                variant="outlined"
                                size="small"
                                name="influencerName"
                                className="w-100"
                                onChange={changeHandler}
                            />
                        </Col>
                    </Col>
                    {/*<Col className="col-auto pr-0">*/}
                    {/*    <Button name="moreOffers" onClick={openExternalSearchPage} className="btn-outline-primary">*/}
                    {/*        {`${t('brand.campaign.book.invite.filters.external_search')}`}*/}
                    {/*    </Button>*/}
                    {/*</Col>*/}
                </div>
                <Col sm={6} lg={6} xl={4} className="d-sm-none px-0 mt-3">
                    <TextField
                        label={`${t('brand.campaign.book.invite.filters.influencer_name')}`}
                        variant="outlined"
                        size="small"
                        name="influencerName"
                        className="w-100"
                        onChange={changeHandler}
                    />
                </Col>
            </Col>
            <InviteContractFilter
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                info={filterInfo}
                userCount={userCount}
                isFilterShown={isFilterShown}
            />
            {!!users?.length
                ? users?.map(user => (
                      <Col className="mb-2 mb-md-4" key={user.id} xl={6}>
                          <InviteContractCardItem openContractModal={handleShow} user={user} />
                      </Col>
                  ))
                : !!users && (
                      <EmptyState
                          desc={t(`brand.campaign.book.invite.filters.no_infl_found`)}
                          iconPath="/icons/emptyBrandCampaignBook.png"
                          addClass="col-lg-5 col-xl-7 mb-4 col-8 text-center mx-auto"
                      />
                  )}
            {totalPages > 1 && (
                <div className="col-12">
                    <div className="d-flex justify-content-end mb-3">
                        <Pagination page={filterParams.page} totalPages={totalPages} handlePagination={handlePages} />
                    </div>
                </div>
            )}
        </Row>
    );
}
